import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import {
	Card,
	Descriptions,
	Spin,
	Tag,
	Typography,
	Result,
	ConfigProvider,
} from "antd";
import dayjs from "dayjs";
import "dayjs/locale/ko";
import locale from "antd/lib/locale/ko_KR";

import PageTitle from "../../../PageTitle";
import { parseKoreaDateTime } from "../../../../utilities/koreaTime";
import {
	RENTAL_CAR_SCHEDULE_TYPE_OPERATION,
	RENTAL_CAR_SCHEDULE_TYPE_RENTAL,
	RENTAL_CAR_SCHEDULE_TYPE_TRUCK,
} from "./rentalCarShare";

dayjs.locale("ko");

const protocol = process.env.REACT_APP_API_PROTOCOL || "http://";
const apiBaseUrl = `${protocol}${process.env.REACT_APP_API_URL}api/`;

const isAllDaySchedule = (schedule) => {
	if (!schedule?.start_at || !schedule?.end_at) {
		return false;
	}
	const start = parseKoreaDateTime(schedule.start_at);
	const end = parseKoreaDateTime(schedule.end_at);
	if (!start.isValid() || !end.isValid()) {
		return false;
	}
	if (!end.isSameOrAfter(start, "day")) {
		return false;
	}
	const startOk =
		start.hour() === 0 &&
		start.minute() === 0 &&
		start.second() === 0;
	const endOk =
		end.hour() === 23 &&
		end.minute() === 59 &&
		(end.second() === 58 || end.second() === 59);
	return startOk && endOk;
};

const formatScheduleRangeLabel = (schedule) => {
	if (schedule.start_at && schedule.end_at) {
		if (isAllDaySchedule(schedule)) {
			const s = parseKoreaDateTime(schedule.start_at);
			const e = parseKoreaDateTime(schedule.end_at);
			if (s.isSame(e, "day")) {
				return `${s.format("YYYY-MM-DD")} 종일`;
			}
			return `${s.format("YYYY-MM-DD")} ~ ${e.format("YYYY-MM-DD")} 종일`;
		}
		return `${parseKoreaDateTime(schedule.start_at).format("YYYY-MM-DD HH:mm")} ~ ${parseKoreaDateTime(schedule.end_at).format("YYYY-MM-DD HH:mm")}`;
	}
	return `${dayjs(schedule.start_date).format("YYYY-MM-DD")} ~ ${dayjs(schedule.end_date).format("YYYY-MM-DD")}`;
};

const renderOptionalText = (value) => {
	const text = value == null ? "" : String(value).trim();
	return text || "—";
};

const ScheduleTags = ({
	scheduleType,
	carTypeName,
	carTypeColor,
	rangeLabel,
}) => (
	<div
		style={{
			display: "flex",
			flexWrap: "wrap",
			gap: 8,
		}}>
		<Tag
			color={
				scheduleType === RENTAL_CAR_SCHEDULE_TYPE_OPERATION
					? "purple"
					: scheduleType === RENTAL_CAR_SCHEDULE_TYPE_TRUCK
						? "geekblue"
						: "cyan"
			}>
			{scheduleType}
		</Tag>
		<Tag color={carTypeColor}>{carTypeName}</Tag>
		<Tag color="blue">{rangeLabel}</Tag>
	</div>
);

const PublicRentalCarSchedulePage = () => {
	const { shareToken } = useParams();
	const [loading, setLoading] = useState(true);
	const [schedule, setSchedule] = useState(null);
	const [notFound, setNotFound] = useState(false);

	useEffect(() => {
		let cancelled = false;

		const fetchSchedule = async () => {
			if (!shareToken) {
				setNotFound(true);
				setLoading(false);
				return;
			}

			setLoading(true);
			setNotFound(false);
			setSchedule(null);

			try {
				const response = await axios.get(
					`${apiBaseUrl}utils/rental/car/schedule/public/${encodeURIComponent(shareToken)}/`,
				);
				if (cancelled) {
					return;
				}
				if (response.status === 200) {
					setSchedule(response.data.rental_car_schedule);
				}
			} catch (error) {
				if (cancelled) {
					return;
				}
				setNotFound(true);
			} finally {
				if (!cancelled) {
					setLoading(false);
				}
			}
		};

		fetchSchedule();

		return () => {
			cancelled = true;
		};
	}, [shareToken]);

	const pageTitle = useMemo(() => {
		if (schedule?.title) {
			return `차량 ${schedule.schedule_type || RENTAL_CAR_SCHEDULE_TYPE_RENTAL} · ${schedule.title}`;
		}
		return "차량 대여 내역";
	}, [schedule]);

	const scheduleType =
		schedule?.schedule_type || RENTAL_CAR_SCHEDULE_TYPE_RENTAL;
	const carTypeName =
		schedule?.custom_car_number?.trim() ||
		schedule?.car_type?.name ||
		"—";
	const carTypeColor = schedule?.car_type?.color || "#2457e6";

	return (
		<ConfigProvider locale={locale}>
			<PageTitle title={pageTitle} />
			<div className="utils-rent_sanctuary__container">
				<div className="utils-rent_sanctuary__header">
					<h1 className="utils-accessed_car__header__title">
						차량 대여 내역
					</h1>
					<Typography.Paragraph
						type="secondary"
						style={{ marginBottom: 0 }}>
						공유 링크로 열린 조회 전용 페이지입니다.
					</Typography.Paragraph>
				</div>

				<Card className="utils-rent_sanctuary__calendar-card">
					{loading ? (
						<div style={{ textAlign: "center", padding: 48 }}>
							<Spin size="large" />
						</div>
					) : notFound || !schedule ? (
						<Result
							status="404"
							title="대여 내역을 찾을 수 없습니다"
							subTitle="링크가 올바른지 확인하거나, 관리자에게 문의해 주세요."
						/>
					) : (
						<>
							<div style={{ marginBottom: 20 }}>
								<ScheduleTags
									scheduleType={scheduleType}
									carTypeName={carTypeName}
									carTypeColor={carTypeColor}
									rangeLabel={formatScheduleRangeLabel(
										schedule,
									)}
								/>
							</div>
							<Descriptions
								bordered
								column={1}
								size="middle">
								<Descriptions.Item label="종류">
									{scheduleType}
								</Descriptions.Item>
								<Descriptions.Item label="차량 종류">
									<Tag color={carTypeColor}>
										{carTypeName}
									</Tag>
								</Descriptions.Item>
								<Descriptions.Item label="일정">
									{formatScheduleRangeLabel(schedule)}
								</Descriptions.Item>
								<Descriptions.Item label="대여 내역">
									{schedule.title}
								</Descriptions.Item>
								<Descriptions.Item label="운전자">
									{renderOptionalText(schedule.driver)}
								</Descriptions.Item>
								<Descriptions.Item label="신청자">
									{renderOptionalText(
										schedule.applicant_name,
									)}
								</Descriptions.Item>
								<Descriptions.Item label="연락처">
									{renderOptionalText(
										schedule.applicant_contact,
									)}
								</Descriptions.Item>
							</Descriptions>
						</>
					)}
				</Card>
			</div>
		</ConfigProvider>
	);
};

export default PublicRentalCarSchedulePage;
